import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import PermissionMatrix from '../../Components/PermissionMatrix';
import DOMAINS from '../../constants/domains';

export default function EmployeeAccess() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [employee, setEmployee] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleDetails, setRoleDetails] = useState({}); // { [roleId]: { permissionIds: [] } }

  // roleByProgram: { [programId]: roleId | '' }
  const [roleByProgram, setRoleByProgram] = useState({});
  // overrides: [{ permissionCode, permissionId, effect }]
  const [overrides, setOverrides] = useState([]);

  // Role bertier FIRST_TIER menyasar Divisi (master_first_tier_program),
  // terpisah dari roleByProgram di atas yang per-program (second-tier).
  // ADA BEBERAPA JENIS role FIRST_TIER yang independen satu sama lain,
  // dibedakan lewat `domain`:
  //   - domain kosong/NULL (MANAGER_PROGRAM) -- dynamic cross-domain
  //     VIEW-only, cuma 1 pilihan role, employee cuma pilih Divisi.
  //   - domain terisi (mis. 'SMS', SMS_MANAGER/STAFF/VIEWER) -- permission
  //     statis, employee pilih Role DAN Divisi, independen dari
  //     MANAGER_PROGRAM (keduanya bisa aktif bersamaan).
  // firstTierAssignments: { [domain]: { roleId, programId } }, domain '' = MANAGER_PROGRAM.
  const [firstTierPrograms, setFirstTierPrograms] = useState([]);
  const [firstTierAssignments, setFirstTierAssignments] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [employeeData, rolesData, accessData, firstTierData] = await Promise.all([
          api.getEmployeeDetail({ id }),
          api.getRoles(), // semua domain -- dipakai untuk dropdown per-program
          api.getEmployeeAccess({ id }),
          api.getFirstTierPrograms(),
        ]);

        setEmployee(employeeData);
        setRoles(rolesData);
        setFirstTierPrograms(firstTierData);

        // Matrix hanya menampilkan domain yang benar-benar relevan untuk
        // karyawan ini (program yang ditugaskan), bukan seluruh domain aplikasi.
        const relevantDomains = [
          ...new Set(employeeData.responsible?.map((p) => p.programCode).filter(Boolean)),
        ];
        const domainsToLoad = relevantDomains.length ? relevantDomains : [DOMAINS[0].code];
        const catalogPerDomain = await Promise.all(
          domainsToLoad.map((domain) => api.getPermissionCatalog({ domain })),
        );
        setCatalog(catalogPerDomain.flat());

        const details = {};
        await Promise.all(
          rolesData.map(async (r) => {
            details[r.id] = await api.getRoleDetail({ id: r.id });
          }),
        );
        setRoleDetails(details);

        const byProgram = {};
        const firstTierByDomain = {};
        accessData.roles.forEach((r) => {
          if (r.roleTier === 'FIRST_TIER') {
            const roleCatalogEntry = rolesData.find((catalogRole) => catalogRole.id === r.roleId);
            const domain = roleCatalogEntry?.domain || '';
            firstTierByDomain[domain] = { roleId: r.roleId, programId: r.firstTierProgramId ?? '' };
            return;
          }
          byProgram[r.programId ?? 'global'] = r.roleId;
        });
        setRoleByProgram(byProgram);
        setFirstTierAssignments(firstTierByDomain);

        setOverrides(
          accessData.overrides.map((o) => ({
            permissionCode: o.permissionCode,
            permissionId: o.permissionId,
            effect: o.effect,
          })),
        );
      } catch (err) {
        console.error(err);
        showToast('Gagal memuat data akses karyawan.', 'danger');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Divisi yang dipilih untuk MANAGER_PROGRAM (domain kosong) bisa menaungi
  // domain yang employee ini belum pernah "ditugaskan" langsung (mis. dia
  // memimpin Divisi yang menaungi BUMDES, tapi tidak ada baris
  // tx_users_program_bcf untuk BUMDES) -- katalog untuk domain itu perlu
  // dimuat juga supaya Effective Permission Matrix bisa menampilkan
  // permission view yang otomatis didapat dari Manager Divisi.
  const managerProgramDivisionId = firstTierAssignments['']?.programId;
  useEffect(() => {
    const selected = firstTierPrograms.find((ftp) => String(ftp.id) === String(managerProgramDivisionId));
    if (!selected) return;

    const knownDomainCodes = DOMAINS.map((d) => d.code);
    const alreadyLoaded = new Set(catalog.map((mod) => mod.programCode));
    const missingDomains = (selected.programCodes || [])
      .filter((code) => knownDomainCodes.includes(code) && !alreadyLoaded.has(code));

    if (!missingDomains.length) return;

    Promise.all(missingDomains.map((domain) => api.getPermissionCatalog({ domain })))
      .then((results) => setCatalog((prev) => [...prev, ...results.flat()]))
      .catch((err) => console.error(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerProgramDivisionId, firstTierPrograms]);

  // Katalog domain lain yang dipegang lewat role FIRST_TIER berdomain (mis.
  // SMS_MANAGER/STAFF/VIEWER) tapi bukan bagian dari program yang
  // ditugaskan langsung ke employee ini -- perlu dimuat terpisah supaya
  // matrix bisa menampilkannya.
  useEffect(() => {
    const domainsToEnsure = Object.keys(firstTierAssignments)
      .filter((domain) => domain && firstTierAssignments[domain]?.roleId);
    const alreadyLoaded = new Set(catalog.map((mod) => mod.programCode));
    const missing = domainsToEnsure.filter((domain) => !alreadyLoaded.has(domain));
    if (!missing.length) return;

    Promise.all(missing.map((domain) => api.getPermissionCatalog({ domain })))
      .then((results) => setCatalog((prev) => [...prev, ...results.flat()]))
      .catch((err) => console.error(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstTierAssignments]);

  const programs = employee?.responsible?.length
    ? employee.responsible
    : [{ programId: 'global', programName: 'Akses Global (tanpa program spesifik)', programCode: DOMAINS[0].code }];

  // Program-program yang sudah otomatis dapat View dari Manager Divisi
  // (MANAGER_PROGRAM, domain kosong) yang dipilih -- Role Assignment untuk
  // program ini tidak perlu diisi lagi (lihat handleSave: baris view yang
  // dari Divisi tidak butuh role per-program terpisah, itu sebabnya section
  // di bawah cuma dipakai untuk eskalasi CRUD lewat Effective Permission
  // Matrix). Ini KHUSUS grup MANAGER_PROGRAM -- role FIRST_TIER berdomain
  // (SMS_*) tidak mengubah Role Assignment second-tier sama sekali, karena
  // domainnya (SMS) tidak tumpang tindih dengan program second-tier CLP/HOL.
  const selectedManagerProgramDivision = firstTierPrograms
    .find((ftp) => String(ftp.id) === String(managerProgramDivisionId));
  const coveredProgramIds = new Set((selectedManagerProgramDivision?.programIds || []).map(String));

  const handleRoleChange = (programId, roleId) => {
    setRoleByProgram((prev) => ({ ...prev, [programId]: roleId }));
  };

  const handleFirstTierChange = (domain, field, value) => {
    setFirstTierAssignments((prev) => ({
      ...prev,
      [domain]: { ...prev[domain], [field]: value },
    }));
  };

  // Union permissionId dari seluruh role yang sedang dipilih: per-program
  // (roleByProgram) DAN semua role FIRST_TIER berdomain (mis. SMS_*) yang
  // dipilih -- permission-nya statis lewat tx_role_permission/roleDetails,
  // sama seperti role second-tier biasa, cuma sumber assignment-nya beda.
  const roleDerivedIds = new Set();
  Object.values(roleByProgram).forEach((roleId) => {
    if (roleId && roleDetails[roleId]) {
      roleDetails[roleId].permissionIds.forEach((pid) => roleDerivedIds.add(pid));
    }
  });
  Object.values(firstTierAssignments).forEach(({ roleId }) => {
    if (roleId && roleDetails[roleId]) {
      roleDetails[roleId].permissionIds.forEach((pid) => roleDerivedIds.add(pid));
    }
  });

  // Permission view yang otomatis didapat dari Manager Divisi (lihat
  // getEffectivePermissions cabang UNION kedua di back-end). MANAGER_PROGRAM
  // SENGAJA tidak punya tx_role_permission statis, jadi kalau tidak dihitung
  // di sini permission ini akan tampak "tidak ada akses" di matrix padahal
  // sebenarnya sudah aktif -- bug yang dilaporkan.
  const divisionViewIds = new Set();
  if (selectedManagerProgramDivision?.programCodes?.length) {
    const divisionDomains = new Set(selectedManagerProgramDivision.programCodes);
    catalog.forEach((mod) => {
      if (!divisionDomains.has(mod.programCode)) return;
      mod.permissions.forEach((perm) => {
        if (perm.action === 'view') divisionViewIds.add(perm.id);
      });
    });
  }

  const allowOverrideIds = new Set(
    overrides.filter((o) => o.effect === 'ALLOW').map((o) => o.permissionId),
  );
  const denyOverrideIds = new Set(
    overrides.filter((o) => o.effect === 'DENY').map((o) => o.permissionId),
  );

  // Efektif = (role ATAU divisi ATAU allow-override) MINUS deny-override. DENY selalu menang.
  const effectiveIds = new Set(
    [...roleDerivedIds, ...divisionViewIds, ...allowOverrideIds].filter((pid) => !denyOverrideIds.has(pid)),
  );

  const sourceById = {};
  divisionViewIds.forEach((pid) => { sourceById[pid] = 'division'; });
  roleDerivedIds.forEach((pid) => { sourceById[pid] = 'role'; });
  allowOverrideIds.forEach((pid) => { sourceById[pid] = 'allow'; });
  denyOverrideIds.forEach((pid) => { sourceById[pid] = 'deny'; });

  const handleTogglePermission = (perm) => {
    const isFromRole = roleDerivedIds.has(perm.id) || divisionViewIds.has(perm.id);
    const existingOverride = overrides.find((o) => o.permissionId === perm.id);

    setOverrides((prev) => {
      const withoutThis = prev.filter((o) => o.permissionId !== perm.id);

      if (isFromRole) {
        // Sedang dari role -> klik pertama = DENY override (mencabut).
        // Klik lagi (sudah DENY) -> hapus override, kembali ke default role.
        if (existingOverride?.effect === 'DENY') return withoutThis;
        return [...withoutThis, { permissionCode: perm.code, permissionId: perm.id, effect: 'DENY' }];
      }

      // Tidak dari role -> klik pertama = ALLOW override.
      // Klik lagi (sudah ALLOW) -> hapus override, kembali ke tidak ada akses.
      if (existingOverride?.effect === 'ALLOW') return withoutThis;
      return [...withoutThis, { permissionCode: perm.code, permissionId: perm.id, effect: 'ALLOW' }];
    });
  };

  const handleResetOverrides = () => setOverrides([]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const rolesPayload = Object.entries(roleByProgram)
        .filter(([, roleId]) => roleId)
        .map(([programId, roleId]) => ({
          roleId: Number(roleId),
          programId: programId === 'global' ? null : Number(programId),
        }));

      // Setiap grup domain FIRST_TIER (MANAGER_PROGRAM di domain kosong,
      // SMS_* di domain 'SMS', dst) dikirim sebagai baris terpisah kalau
      // keduanya (roleId + programId/Divisi) sudah dipilih -- independen
      // satu sama lain, employee bisa pegang lebih dari satu sekaligus.
      Object.values(firstTierAssignments).forEach(({ roleId, programId }) => {
        if (roleId && programId) {
          rolesPayload.push({ roleId: Number(roleId), programId: Number(programId) });
        }
      });

      const overridesPayload = overrides.map((o) => ({
        permissionCode: o.permissionCode,
        effect: o.effect,
      }));

      await api.updateEmployeeAccess({ id, roles: rolesPayload, overrides: overridesPayload });

      navigate(`/employee/${id}`, {
        state: { toastMessage: 'Akses karyawan berhasil diperbarui!', toastType: 'success' },
      });
    } catch (err) {
      console.error(err);
      showToast(`Gagal menyimpan akses: ${err.message || err}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !employee) {
    return (
      <div className="container py-5 text-center text-muted">
        <div className="spinner-border text-primary mb-3" />
        <p>Memuat pengaturan akses...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/employee" className="text-decoration-none">Employees</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/employee/${id}`} className="text-decoration-none">{employee.firstName}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">Akses & Permission</li>
        </ol>
      </nav>

      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1">Atur Akses</h4>
          <p className="text-muted mb-0">
            Role dan permission untuk{' '}
            <span className="text-primary fw-semibold">{employee.firstName} {employee.lastName}</span>
          </p>
        </div>
        <button
          onClick={() => navigate(`/employee/${id}`)}
          className="btn btn-outline-secondary rounded-pill px-4 btn-sm fw-medium"
        >
          <i className="bi bi-arrow-left me-1"></i> Kembali
        </button>
      </div>

      {/* ROLE FIRST-TIER (per domain, independen satu sama lain) */}
      {Object.entries(
        roles
          .filter((r) => r.tier === 'FIRST_TIER')
          .reduce((groups, r) => {
            const domain = r.domain || '';
            (groups[domain] = groups[domain] || []).push(r);
            return groups;
          }, {}),
      ).map(([domain, rolesInGroup]) => {
        const isManagerProgramGroup = domain === '';
        const assignment = firstTierAssignments[domain] || {};
        const groupTitle = isManagerProgramGroup ? 'Manager Divisi' : `Akses ${domain}`;
        const groupDescription = isManagerProgramGroup
          ? 'Kalau karyawan ini memimpin sebuah Divisi (bukan cuma satu Program), pilih Divisi-nya di sini. Dia otomatis dapat akses baca (view) ke SELURUH Program/Sub-Divisi di bawah Divisi itu — termasuk yang ditambahkan belakangan — tanpa perlu diatur satu-satu di bawah. Untuk akses tulis (create/update/delete/approve) di program tertentu, tambahkan lewat matrix di bawah seperti biasa.'
          : `Akses ke modul ${domain} dibatasi sesuai Divisi yang dipimpin/ditugaskan — pilih Role dan Divisi-nya di sini. Independen dari assignment lain di atas.`;

        return (
          <div key={domain} className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
            <div className="card-header bg-white border-0 pt-4 px-4">
              <h6 className="fw-bold mb-0">{groupTitle}</h6>
              <small className="text-muted">{groupDescription}</small>
            </div>
            <div className="card-body p-4 d-flex gap-3 flex-wrap">
              {!isManagerProgramGroup && (
                <select
                  className="form-select form-select-sm rounded-pill"
                  style={{ maxWidth: '220px' }}
                  value={assignment.roleId || ''}
                  onChange={(e) => handleFirstTierChange(domain, 'roleId', e.target.value)}
                >
                  <option value="">— Tidak ada role —</option>
                  {rolesInGroup.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              )}
              <select
                className="form-select form-select-sm rounded-pill"
                style={{ maxWidth: '360px' }}
                value={assignment.programId || ''}
                onChange={(e) => {
                  const nextId = e.target.value;
                  // Untuk MANAGER_PROGRAM (role tunggal), memilih Divisi
                  // otomatis set roleId-nya juga.
                  handleFirstTierChange(domain, 'programId', nextId);
                  if (isManagerProgramGroup && nextId) {
                    handleFirstTierChange(domain, 'roleId', rolesInGroup[0].id);
                  }
                  if (isManagerProgramGroup) {
                    // Bersihkan role per-program yang sekarang jadi redundan
                    // (sudah tercakup view dari Divisi yang baru dipilih).
                    const nextFtp = firstTierPrograms.find((ftp) => String(ftp.id) === String(nextId));
                    const nextCovered = new Set((nextFtp?.programIds || []).map(String));
                    setRoleByProgram((prev) => Object.fromEntries(
                      Object.entries(prev).filter(([programId]) => !nextCovered.has(String(programId))),
                    ));
                  }
                }}
              >
                <option value="">{isManagerProgramGroup ? '— Bukan Manager Divisi —' : '— Tidak ada Divisi —'}</option>
                {firstTierPrograms.map((ftp) => (
                  <option key={ftp.id} value={ftp.id}>
                    {ftp.name}{ftp.second_programs ? ` (${ftp.second_programs})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      })}

      {/* ROLE ASSIGNMENT */}
      <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
        <div className="card-header bg-white border-0 pt-4 px-4">
          <h6 className="fw-bold mb-0">Role Assignment</h6>
          <small className="text-muted">
            {selectedManagerProgramDivision
              ? 'Program yang sudah tercakup Manager Divisi tidak perlu diatur lagi di sini — cuma dipakai untuk program DI LUAR Divisi tersebut, atau kalau butuh akses lebih dari view (pakai Effective Permission Matrix di bawah).'
              : 'Tetapkan role per program yang dikelola karyawan ini'}
          </small>
        </div>
        <div className="card-body p-4">
          {programs.map((prog) => {
            const rolesForDomain = roles.filter((r) => r.domain === prog.programCode);
            const domainUnknown = !prog.programCode;
            const isCoveredByDivision = coveredProgramIds.has(String(prog.programId));

            if (isCoveredByDivision) {
              return (
                <div key={prog.programId} className="d-flex align-items-center justify-content-between border border-success-subtle bg-success bg-opacity-10 rounded-3 p-3 mb-2">
                  <div>
                    <span className="fw-medium">{prog.programName}</span>
                    {prog.programCode && (
                      <span className="badge bg-light text-secondary border ms-2" style={{ fontSize: '0.65rem' }}>
                        {prog.programCode}
                      </span>
                    )}
                  </div>
                  <span className="badge bg-success bg-opacity-75 rounded-pill px-3 py-2">
                    <i className="bi bi-eye-fill me-1"></i> View otomatis dari Manager Divisi
                  </span>
                </div>
              );
            }

            return (
              <div key={prog.programId} className="d-flex align-items-center justify-content-between border rounded-3 p-3 mb-2">
                <div>
                  <span className="fw-medium">{prog.programName}</span>
                  {prog.programCode && (
                    <span className="badge bg-light text-secondary border ms-2" style={{ fontSize: '0.65rem' }}>
                      {prog.programCode}
                    </span>
                  )}
                  {domainUnknown && (
                    <div>
                      <small className="text-warning">
                        <i className="bi bi-exclamation-triangle-fill me-1"></i>
                        Domain program ini belum dikenal RBAC
                      </small>
                    </div>
                  )}
                </div>
                <select
                  className="form-select form-select-sm rounded-pill"
                  style={{ maxWidth: '220px' }}
                  value={roleByProgram[prog.programId] || ''}
                  disabled={domainUnknown}
                  onChange={(e) => handleRoleChange(prog.programId, e.target.value)}
                >
                  <option value="">— Tidak ada role —</option>
                  {rolesForDomain.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>

      {/* EFFECTIVE MATRIX */}
      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="card-header bg-white border-0 pt-4 px-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h6 className="fw-bold mb-0">Effective Permission Matrix</h6>
              <small className="text-muted">
                <span className="badge bg-primary me-1">biru</span> dari role ·{' '}
                <span className="badge bg-info text-white me-1">cyan</span> view otomatis dari Manager Divisi ·{' '}
                <span className="badge bg-success me-1">hijau</span> override ALLOW ·{' '}
                <span className="badge bg-danger me-1">merah</span> override DENY
              </small>
            </div>
          </div>
        </div>
        <div className="card-body p-4">
          <PermissionMatrix
            catalog={catalog}
            selectedIds={effectiveIds}
            sourceById={sourceById}
            onToggle={handleTogglePermission}
          />
          <div className="d-flex justify-content-end mt-4 gap-2">
            <button
              className="btn btn-light rounded-pill px-4 btn-sm fw-medium text-muted"
              onClick={handleResetOverrides}
              disabled={!overrides.length}
            >
              Reset Override
            </button>
            <button
              className="btn btn-primary rounded-pill px-4 btn-sm fw-medium shadow-sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Menyimpan...
                </>
              ) : (
                <>
                  <i className="bi bi-shield-check me-1"></i> Simpan Akses
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
